import React, { useRef, useState } from "react";
import { zipSync } from "fflate";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Package } from "lucide-react";
import { toast } from "sonner";
import { SheetMusic, isFillMeasure } from "@/components/SheetMusic";
import { buildClipLayout, recordClip, defaultSection } from "@/components/ClipMaker";
import { useCaption } from "@/lib/useCaption";
import api from "@/lib/api";

const noop = () => {};
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const safe = (s) => String(s || "clip").replace(/[^a-z0-9]/gi, "_");

const pickSection = (tab, rule) => {
  if (rule === "fill") {
    const i = (tab.sections || []).findIndex((s) => (s.measures || []).some((m) => isFillMeasure(s, m)));
    if (i >= 0) return i;
  }
  return parseInt(defaultSection(tab), 10);
};

export const ClipPack = ({ saved, onClose }) => {
  const [picked, setPicked] = useState(() => new Map());
  const [len, setLen] = useState("30");
  const [sharing, setSharing] = useState(false);
  const [countIn, setCountIn] = useState(true);
  const [caption, changeCaption] = useCaption();
  const [cur, setCur] = useState(null);
  const [status, setStatus] = useState(null);
  const sheetApi = useRef(null);
  const canvasRef = useRef(null);
  const cancelRef = useRef(null);
  const stopRef = useRef(false);
  const busy = !!status;

  const toggle = (it) => setPicked((s) => { const n = new Map(s); if (n.has(it.id)) n.delete(it.id); else n.set(it.id, String(pickSection(it.tab, "groove"))); return n; });
  const setSection = (id, v) => setPicked((s) => new Map(s).set(id, v));

  const sharePack = async () => {
    setSharing(true);
    try {
      const items = saved.filter((it) => picked.has(it.id)).map((it) => ({ tab: it.tab, section: parseInt(picked.get(it.id), 10) }));
      const { data } = await api.post("/packs", { caption, items });
      const url = `${window.location.origin}/p/${data.id}`;
      try { await navigator.clipboard.writeText(url); toast.success("Practice pack link copied — send it to your students"); } catch (e) { toast.success(`Practice pack ready: ${url}`); }
      setPackUrl(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the practice pack");
    } finally {
      setSharing(false);
    }
  };
  const [packUrl, setPackUrl] = useState("");

  const run = async () => {
    if (!window.MediaRecorder || !HTMLCanvasElement.prototype.captureStream) { toast.error("Your browser can't record video — try Chrome or Safari"); return; }
    const items = saved.filter((it) => picked.has(it.id));
    const L = parseInt(len, 10);
    const files = {};
    stopRef.current = false;
    try {
      for (let i = 0; i < items.length; i++) {
        if (stopRef.current) break;
        const tab = items[i].tab;
        const spm = tab.steps_per_measure || 16;
        const beats = parseInt(tab.time_signature, 10) || 4;
        const den = parseInt(String(tab.time_signature || "4/4").split("/")[1], 10) || 4;
        const layout = buildClipLayout(tab, parseInt(picked.get(items[i].id), 10), spm, beats);
        setCur({ layout, spm, beats, den });
        setStatus({ i, n: items.length, pct: 0, title: tab.title });
        await wait(700);
        if (stopRef.current) break;
        const res = await recordClip({ tab, layout, api: sheetApi.current, canvas: canvasRef.current, L, countIn, caption, beats,
          onProgress: (p) => setStatus({ i, n: items.length, pct: p, title: tab.title }), cancelRef, stopRef });
        if (!res || stopRef.current) break;
        let name = `${safe(tab.title)}_${safe(layout.sec.name)}_beatflow.${res.ext}`;
        for (let k = 2; files[name]; k++) name = `${safe(tab.title)}_${k}_beatflow.${res.ext}`;
        files[name] = [new Uint8Array(await res.blob.arrayBuffer()), { level: 0 }];
      }
      const names = Object.keys(files);
      if (names.length) {
        const blob = new Blob([zipSync(files)], { type: "application/zip" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `BeatFlow_clip_pack_${names.length}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
        toast.success(`Clip pack saved (${names.length} clip${names.length > 1 ? "s" : ""})`);
      }
    } catch (e) {
      toast.error("Could not finish the clip pack");
    } finally {
      setStatus(null);
      setCur(null);
    }
  };

  const cancel = () => { stopRef.current = true; if (cancelRef.current) cancelRef.current(); };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="bg-[#0e121a] border-white/10 text-slate-100 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="clip-pack-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider flex items-center gap-2"><Package className="w-5 h-5 text-[#ffab00]" /> Student clip pack</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Tick songs and choose a part for each. Record video clips as one ZIP, or share a link so students practise the same parts inside BeatFlow.</DialogDescription>
        </DialogHeader>
        <div className="max-h-48 overflow-y-auto flex flex-col gap-1.5 pr-1" data-testid="clip-pack-list">
          {saved.map((it) => (
            <div key={it.id} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${picked.has(it.id) ? "border-[#ffab00]/50 bg-[#ffab00]/[0.05]" : "border-white/10"}`}>
              <Checkbox checked={picked.has(it.id)} onCheckedChange={() => toggle(it)} disabled={busy} data-testid={`clip-pack-check-${it.id}`} />
              <span className="text-sm truncate flex-1 cursor-pointer" onClick={() => !busy && toggle(it)}>{it.tab.title}<span className="text-slate-500"> · {it.tab.artist}</span></span>
              {picked.has(it.id) && (
                <Select value={picked.get(it.id)} onValueChange={(v) => setSection(it.id, v)} disabled={busy}>
                  <SelectTrigger className="h-7 w-36 bg-white/5 border-white/15 text-[11px]" data-testid={`clip-pack-section-${it.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(it.tab.sections || []).map((sec, i) => <SelectItem key={i} value={String(i)}>{sec.name || `Section ${i + 1}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPicked(new Map(saved.map((it) => [it.id, String(pickSection(it.tab, "groove"))])))} disabled={busy} className="text-[11px] text-[#00f0ff] hover:underline" data-testid="clip-pack-all-groove">All: main groove</button>
          <button onClick={() => setPicked((s) => new Map([...s.keys()].map((id) => [id, String(pickSection(saved.find((x) => x.id === id).tab, "fill"))])))} disabled={busy || !picked.size} className="text-[11px] text-[#00f0ff] hover:underline" data-testid="clip-pack-all-fill">Ticked: first fill</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[10px] font-tab uppercase tracking-widest text-slate-500">Length
            <Select value={len} onValueChange={setLen} disabled={busy}>
              <SelectTrigger className="mt-1 bg-white/5 border-white/15 text-xs" data-testid="clip-pack-length-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["15", "30", "60"].map((v) => <SelectItem key={v} value={v}>{v} seconds</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
        </div>
        <label className="text-[10px] font-tab uppercase tracking-widest text-slate-500">Your caption (optional)
          <Input value={caption} onChange={(e) => changeCaption(e.target.value)} disabled={busy} placeholder="e.g. Lessons with Tony · DrumKitPro" className="mt-1 bg-white/5 border-white/15 text-sm normal-case tracking-normal" data-testid="clip-pack-caption-input" />
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <Switch checked={countIn} onCheckedChange={setCountIn} disabled={busy} data-testid="clip-pack-countin-switch" /> Count-in (1 2 3 4 with clicks)
        </label>
        <canvas ref={canvasRef} width={1080} height={1920} className={`mx-auto rounded-lg border border-white/10 ${busy ? "" : "hidden"}`} style={{ width: 135, height: 240 }} data-testid="clip-pack-canvas" />
        {packUrl && (
          <div className="text-[11px] text-slate-400 break-all" data-testid="clip-pack-share-url">Student link: <a href={packUrl} target="_blank" rel="noreferrer" className="text-[#00f0ff] underline">{packUrl}</a></div>
        )}
        {status && (
          <div className="flex flex-col gap-1" data-testid="clip-pack-progress">
            <Progress value={status.pct} className="h-2" />
            <span className="text-[11px] text-slate-400">Clip {status.i + 1} of {status.n}: {status.title} — {status.pct}% · keep this tab open</span>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          {busy ? (
            <Button variant="ghost" onClick={cancel} data-testid="clip-pack-cancel">Stop</Button>
          ) : (
            <Button variant="ghost" onClick={onClose} data-testid="clip-pack-close">Close</Button>
          )}
          <Button variant="outline" onClick={sharePack} disabled={busy || sharing || !picked.size} className="border-[#00f0ff]/40 text-[#00f0ff] bg-transparent" data-testid="clip-pack-share-link">
            {sharing ? "Creating…" : "Share as practice pack"}
          </Button>
          <Button onClick={run} disabled={busy || !picked.size} className="bg-[#ffab00] text-black hover:bg-[#ffc233]" data-testid="clip-pack-record">
            Record {picked.size || ""} clip{picked.size === 1 ? "" : "s"} (~{Math.ceil((picked.size * (parseInt(len, 10) + 3)) / 60)} min)
          </Button>
        </div>
        {cur && (
          <div style={{ position: "fixed", left: -20000, top: 0, width: 700, background: "#f7f4ec" }} aria-hidden>
            <SheetMusic sections={cur.layout.sections} spm={cur.spm} beats={cur.beats} den={cur.den} width={700} zoom={1} apiRef={sheetApi} onSeek={noop} sticking />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
